import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import moment from 'moment';
import {
  Comment, Icon, List, message, Tooltip
} from 'antd';
import ExtendedAvatar from 'components/extendedAvatar/extended-avatar';
import settings from 'settings';
import { getFieldFilters, getSortField } from 'library/helpers';
import { Layout, Prompt } from 'components/Page';
import {
  add, dirty, init, list, load, remove, update
} from 'actions';
import AuthService from 'library/authService/auth-service';

const me = AuthService.whoami();

const { APP_CONFIG } = settings;
/**
 * @description Map data and server flight
 * @param {state} Object
 * @param {owProps} Object
 */
const mapStateToProps = (state, ownProps) => ({
  item: state.api[ownProps.reducer.item.key].data,
  comments: state.api[ownProps.reducer.list.key].data.results,
  total: state.api[ownProps.reducer.list.key].data.count,
  isDirty: state.api[ownProps.reducer.list.key].isDirty,
  inFlight: state.api[ownProps.reducer.list.key].inFlight,
  formInFlight: state.api[ownProps.reducer.item.key].inFlight,
  formError: state.api[ownProps.reducer.item.key].error
});
/**
 * @description Dispatch create, loadList, load, deleteItem
 * @param {dispatch} Object
 */
const mapDispatchToProps = (dispatch, ownProps) => ({
  create() {
    dispatch(init(ownProps.reducer.item.key, { active: true, ...ownProps.config.init }));
  },
  loadList(page = 1) {
    const sorted = getSortField(ownProps.config.list.sorter);
    const filters = getFieldFilters(ownProps.config.list.filters);
    dispatch(list(
      ownProps.reducer.list.key, {
        page: page - 1,
        pageSize: APP_CONFIG.defaultTablePageSmallSize,
        filters,
        sorted
      }
    ))
      .then(() => (ownProps.onLoadList ? ownProps.onLoadList() : true));
  },
  loadItem(data) {
    const { id } = data;
    return dispatch(load(ownProps.reducer.item.key, { id }))
      .then(() => (ownProps.onLoadItem ? ownProps.onLoadItem(id) : true));
  },
  reloadList() {
    dispatch(dirty(ownProps.reducer.list.key));
  },
  saveItem(id, data) {
    return dispatch(id > 0 ? update(ownProps.reducer.item.key, { id, ...data }) : add(ownProps.reducer.item.key, data));
  },
  deleteItem(id) {
    return dispatch(remove(ownProps.reducer.item.key, { id }));
  }
});
/**
 * @description Launch the Prompt to Save or Discard changes
 * @param {Save} Function
 */
const promptSaveChanges = ({ onOkClick, onCancelClick }) => (
  Prompt({
    onOkClick,
    onCancelClick
  })
);
/**
 * @description Launch the Prompt to Save or Discard changes
 * @param {Save} Function
 */
const promptDeleteItem = ({
  onOkClick, onCancelClick, content, okText, cancelText, title
}) => (
  Prompt({
    content,
    okText,
    cancelText,
    title,
    onOkClick,
    onCancelClick
  })
);
/**
 * Comments Layout
 * @description Layout for comments
 * @version 1.0.0
 */
const Comments = ({
  canAdmin,
  classes,
  clearErrors,
  config,
  create,
  defaultSize,
  deleteItem,
  formError,
  formInFlight,
  isDirty,
  item,
  comments,
  inFlight,
  loadItem,
  loadList,
  messaging,
  onPrepareData,
  onSuccessCallback,
  onFailureCallback,
  reloadList,
  resetItem,
  saveItem,
  total,
  permissions
}) => {
  const [currentPage, setPage] = useState(1);
  useEffect(() => {
    loadList(currentPage, config.list.filters, config.list.sorted);
    create();
  }, [currentPage, config.list.filters, isDirty]);
  // Is the form in a state of edit or not
  const isEditMode = (item.id !== null && item.id > 0) || false;
  /**
   * @description Save the item and dislpay a relevant message
   * @param {id} Number
   * @param {data} Object
   * @param {prevData} Object
   * @triggers {reloadList}
   */
  const handleSave = (id, data, prevData = null) => {
    const preparedData = onPrepareData(data);
    const postSuccessCallback = () => {
      message.success(messaging.save.success(data));
      prevData ? loadItem(prevData.id) : resetItem(); // load previous data or reset the loaded item
    };
    // null data aborts save, but still call callback
    if (preparedData === null) {
      return onSuccessCallback(null, data, item, postSuccessCallback);
    }
    return saveItem(id, preparedData)
      .then((response) => {
        onSuccessCallback(response, data, item, postSuccessCallback);
        reloadList(); // reload change list data
        create();
        return response;
      },
      (error) => {
        onFailureCallback(error, () => {
          message.error(messaging.save.failure(data));
        });
        return error;
      });
  };
  /**
   * @description Delete the item and dislpay a relevant message
   * @param {id} Number
   * @triggers {reloadList}
   */
  const handleDelete = id => deleteItem(id)
    .then((response) => {
      if (response.status === 204) {
        message.success(messaging.delete.success());
        resetItem(); // remove item data from store
      }
      else {
        message.success(messaging.delete.disabled());
        loadItem(response.id); // reload the current item to reflect the inactive field
      }
      reloadList(); // reload the list of items to reflect the changes in related  data
    })
    .catch((error) => {
      message.error(messaging.delete.failure(error));
    });
  /**
   * @description Popup a confirmation asking to delete or cancel
   * @param {item} Object
   */
  const deleteConfirm = (comment) => {
    promptDeleteItem({
      onOkClick() {
        handleDelete(comment);
      },
      onCancelClick() {
      },
      content: config.list.confirmMessage(),
      okText: 'Yes - Delete',
      cancelText: 'No - Cancel',
      title: 'Delete Comment?'
    });
  };
  /**
   * @description Check to find item exsists in data first
   * @param {itemId} number
   * @triggers {deleteConfirm}
   */
  const deleteCheck = (commentId) => {
    if (commentId >= 0) {
      if (comments.find(comment => comment.id === commentId)) {
        deleteConfirm(commentId);
      }
    }
  };
  /**
   * @description Check if the form is dirty
   * @triggers {promptSaveChanges}
   */
  const handleFormClose = (currentItem, previousItem) => {
    // Prompt to save the changed form data
    promptSaveChanges({
      onOkClick() {
        handleSave(previousItem.id, previousItem);
      },
      onCancelClick() {
      }
    });
  };
  const getComments = () => (
    <div className="bhp-container">
      <div className="bhp-header-bar">
        <div className="bhp-title bhp-capitalize">{config.list.title}</div>
      </div>
      <List
        className="bhp-container bhp-comments"
        loading={{
          indicator: <Icon type="loading" spin />,
          tip: 'Loading...',
          spinning: inFlight
        }}
        size="small"
        dataSource={comments}
        renderItem={(comment) => {
          const CanEdit = permissions.CanAdminComments()
            .hasPermissions() || comment.created_user.id === me.id;
          const CanDelete = permissions.CanAdminComments()
            .hasPermissions();
          return (
            <List.Item
              actions={[comment.hidden && (<Icon type="eye-invisible" />)]}
              onClick={() => canAdmin && loadItem(comment)}
              className={classNames({ 'bhp-comments-active': comment.id === item.id })}
            >
              <Comment
                key={comment.id}
                actions={[
                  CanEdit && (
                    <span onClick={() => loadItem(comment)}>
                      <Icon type="edit" />
                      Edit
                    </span>
                  ),
                  CanDelete && (
                    <span onClick={() => deleteCheck(comment.id)}>
                      <Icon type="delete" />
                      Delete
                    </span>
                  )
                ]}
                author={`${comment.created_user.first_name} ${comment.created_user.last_name}`}
                avatar={<ExtendedAvatar name={`${comment.created_user.first_name} ${comment.created_user.last_name}`} />}
                content={comment.obfuscated_text}
                datetime={(
                  <Tooltip title={moment(comment.created_date)
                    .format('YYYY-MM-DD HH:mm:ss')}
                  >
                    <span>
                      {moment(comment.created_date)
                        .fromNow()}
                    </span>
                  </Tooltip>
                )}
                width="100%"
              />
            </List.Item>
          );
        }}
        pagination={{
          onChange: page => setPage(page),
          current: currentPage,
          pageSize: APP_CONFIG.defaultTablePageSmallSize,
          total,
          size: 'small',
          hideOnSinglePage: true,
          showTotal: records => `Total ${records} items`
        }}
      />
    </div>
  );
  /**
   * @description Get Form Wrapper
   * @returns {Form}
   */
  const getForm = () => (
    <div className="bhp-container">
      {
        config.detail.component({
          canAdmin: permissions.CanAdminComments()
            .hasPermissions() || canAdmin,
          dataSource: item,
          formError,
          inFlight: formInFlight,
          isEditMode,
          onSaveClick: handleSave,
          onCancelClick: create,
          onFormClose: handleFormClose,
          onPopupOk: clearErrors,
          popupError: true,
          title: config.detail.title
        })
      }
    </div>
  );
  return (
    // Layout Component to build out the Page
    <Layout
      classes={classes}
      component1={getComments()}
      component2={getForm()}
      split="horizontal"
      defaultSize={defaultSize}
    />
  );
};

Comments.propTypes = {
  canAdmin: PropTypes.bool,
  isDirty: PropTypes.bool,
  classes: PropTypes.string,
  clearErrors: PropTypes.func,
  config: PropTypes.object,
  create: PropTypes.func,
  deleteItem: PropTypes.func,
  formError: PropTypes.object,
  formInFlight: PropTypes.bool,
  item: PropTypes.object,
  comments: PropTypes.array,
  inFlight: PropTypes.bool,
  loadItem: PropTypes.func,
  loadList: PropTypes.func,
  onPrepareData: PropTypes.func,
  onSuccessCallback: PropTypes.func,
  onFailureCallback: PropTypes.func,
  messaging: PropTypes.object,
  resetItem: PropTypes.func,
  saveItem: PropTypes.func,
  reloadList: PropTypes.func,
  total: PropTypes.number,
  defaultSize: PropTypes.string,
  permissions: PropTypes.object
};

Comments.defaultProps = {
  canAdmin: false,
  isDirty: false,
  classes: '',
  config: {},
  clearErrors() {
  },
  create() {
  },
  defaultSize: '45%',
  deleteItem() {
  },
  formError: {},
  formInFlight: false,
  item: {},
  comments: [],
  inFlight: false,
  loadItem() {
  },
  loadList() {
  },
  messaging: {
    delete: {
      disabled: () => 'Item Disabled.',
      failure: error => `Delete Failed. ${error}`,
      success: () => 'Delete Successful.'
    },
    save: {
      failure: () => 'Saving failed',
      success: () => 'Saved successfully'
    }
  },
  onPrepareData(data) {
    return data;
  },
  onSuccessCallback(response, data, original, callback) {
    callback();
  },
  onFailureCallback(response, callback) {
    callback();
  },
  resetItem() {
  },
  saveItem() {
  },
  reloadList() {
  },
  total: 0,
  permissions: {}
};

export default connect(mapStateToProps, mapDispatchToProps)(Comments);
